import { createContext, useContext, type ReactNode } from 'react';
import { seasonHasStarted } from '@ff/core';
import { useSeason } from './useSeason.ts';
import { useSeasonSelect } from './seasonSelect.tsx';

type CurrentSeason = ReturnType<typeof useSeason> & {
  /**
   * At least one game has kicked off this season. Results only carry games that
   * have started or finished, so an empty results list means "pre-season" — the
   * signal that gates (and archives) the War Room.
   */
  seasonStarted: boolean;
};

const Ctx = createContext<CurrentSeason | null>(null);

/**
 * Loads the selected season once, above the router, so BOTH the nav (in Shell)
 * and the pages (below SeasonBoundary) read the same bundle. Without this the
 * nav couldn't know whether the season had started, and we'd fetch it twice.
 */
export function CurrentSeasonProvider({ children }: { children: ReactNode }) {
  const { selected } = useSeasonSelect();
  const season = useSeason(selected ?? 0);
  const seasonStarted = season.data ? seasonHasStarted(season.data.bundle.data) : false;
  return <Ctx.Provider value={{ ...season, seasonStarted }}>{children}</Ctx.Provider>;
}

export function useCurrentSeason(): CurrentSeason {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCurrentSeason must be used within CurrentSeasonProvider');
  return v;
}
